import { useState, useEffect, useCallback } from 'react';
import { getPricingPlans } from '../../config/pricingPlans';
import { getProducts, createCheckoutSession, getSubscription, createManualCheckoutSession, getAllPrices, resetCustomer, getStripePlans } from '../../services/stripeService';
import { auth } from '../../config/firebase';
import { getStripe, STRIPE_PRICE_IDS, STRIPE_PRODUCT_IDS } from '../../config/stripe';
import { getGeneralSettings } from '../../services/systemSettingsService';
import { trackSubscription } from '../../services/analyticsService';

// Helper function to check billing period validity
const isValidBillingPeriod = (period) => {
  return period === 'monthly' || period === 'year';
};

// Default value for invalid billing period
const DEFAULT_BILLING_PERIOD = 'monthly';

// Helper function to get valid billing period
const getValidBillingPeriod = (period) => {
  return isValidBillingPeriod(period) ? period : DEFAULT_BILLING_PERIOD;
};

// Handle Stripe errors
const handleStripeError = async (error) => {
  console.error("Stripe error handler activated:", error);

  // Check if "No such customer" error
  if (error.message && error.message.includes("No such customer")) {
    console.log("Detected 'No such customer' error, suggesting reset");

    // Ask user if they want to reset account data
    if (window.confirm('There seems to be an issue with your Stripe customer account. Would you like to reset your data and try again?')) {
      try {
        const resetSuccess = await resetCustomer();

        if (resetSuccess) {
          alert('Customer data reset successfully. The system will attempt to subscribe again.');
          return true; // Allow retry of subscription process
        } else {
          alert('We could not reset your customer data. Please try again later or contact support.');
          return false;
        }
      } catch (resetError) {
        console.error("Error during customer reset:", resetError);
        alert('An error occurred while trying to reset your customer data. Please contact support.');
        return false;
      }
    }
  }

  // Other errors
  return false;
};

const usePricingLogic = () => {
  // State management for selected billing period
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  // State management for pricing plans with initial values
  const [plans, setPlans] = useState(getPricingPlans('monthly'));

  // State management for Stripe products
  const [stripeProducts, setStripeProducts] = useState([]);

  // State management for active subscription of the user
  const [activeSubscription, setActiveSubscription] = useState(null);

  // State management for loading
  const [loading, setLoading] = useState(true);

  // State management for payment processing
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // State management for price IDs
  const [priceIds, setPriceIds] = useState(STRIPE_PRICE_IDS);

  // State management for development mode
  const [developerMode, setDeveloperMode] = useState(false);

  // State management for plan source - whether using Stripe plans or local plans
  const [useStripePlans, setUseStripePlans] = useState(false);

  // Function to switch between Stripe plans and local plans
  const togglePlanSource = useCallback(async () => {
    setLoading(true);
    try {
      const newUseStripePlans = !useStripePlans;
      setUseStripePlans(newUseStripePlans);

      if (newUseStripePlans) {
        // Load Stripe plans
        const stripePlans = await getStripePlans(billingPeriod);
        if (stripePlans) {
          console.log('Setting plans from Stripe:', stripePlans);
          setPlans(stripePlans);
        } else {
          // If no Stripe plans are available, use local plans
          console.log('No Stripe plans available, using local plans');
          setPlans(getPricingPlans(billingPeriod));
          // Switch back to local plans
          setUseStripePlans(false);
          alert('Could not load plans from Stripe. Using local plans instead.');
        }
      } else {
        // Use local plans
        console.log('Using local pricing plans');
        setPlans(getPricingPlans(billingPeriod));
      }
    } catch (error) {
      console.error('Error toggling plan source:', error);
      // In case of error, use local plans
      setPlans(getPricingPlans(billingPeriod));
      setUseStripePlans(false);
      alert('Error loading plans from Stripe. Using local plans instead.');
    } finally {
      setLoading(false);
    }
  }, [useStripePlans, billingPeriod]);

  // Function to change billing period
  const toggleBillingPeriod = useCallback((period) => {
    setBillingPeriod(period);
  }, []);

  // Helper function to create Checkout Session and redirect to checkout page
  const createAndRedirectToCheckout = useCallback(async (finalPriceId, successUrl, cancelUrl, planType) => {
    console.log(`Creating checkout session with price ID: ${finalPriceId}`);
    console.log(`Success URL: ${successUrl}, Cancel URL: ${cancelUrl}`);
    console.log(`Plan type: ${planType}`);

    // Create a new session with the price ID
    const { sessionId, url } = await createCheckoutSession(
      finalPriceId,
      successUrl,
      cancelUrl
    );

    console.log("Got checkout session:", { sessionId, url });

    // Redirect to Stripe checkout page using the session URL
    if (url) {
      console.log("Redirecting to Stripe checkout page via URL...");
      window.location.href = url;
    } else if (sessionId) {
      // Fallback for older Firebase extension versions that only return sessionId
      console.log("No URL returned, falling back to redirectToCheckout with sessionId...");
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error("Failed to load Stripe");
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error("Stripe checkout error:", error);
        throw error;
      }
    } else {
      throw new Error("No checkout URL or session ID returned from Stripe");
    }
  }, []);

  // Function to create Checkout Session and redirect to checkout page for Stripe
  const handleSubscribe = useCallback(
    async (priceId, billingPeriod, planType = 'pro') => {
      setIsProcessingPayment(true);

      try {
        if (!auth.currentUser) {
          console.error("User not logged in");
          alert("You must log in to subscribe");
          setIsProcessingPayment(false);
          return;
        }

        // Handle free plan - redirect to dashboard immediately
        if (planType === 'free') {
          console.log("Free plan selected, redirecting to dashboard");
          setIsProcessingPayment(false);
          window.location.href = "/dashboard";
          return;
        }

        console.log("Using Stripe checkout with price ID");
        console.log(`Received params - Price ID: ${priceId}, Billing Period: ${billingPeriod}, Plan Type: ${planType}`);

        // Validate billing period (only for recurring plans)
        let finalPriceId;

        if (planType === 'transfer') {
          // For transfer, use one-time price ID
          finalPriceId = priceIds.TRANSFER_ONETIME;
          console.log('Using transfer one-time price ID:', finalPriceId);
        } else {
          // For other plans, validate billing period
          const validBillingPeriod = getValidBillingPeriod(billingPeriod);
          if (validBillingPeriod !== billingPeriod) {
            console.warn(`Invalid billing period received: ${billingPeriod}, using: ${validBillingPeriod}`);
            billingPeriod = validBillingPeriod;
          }

          // Select price ID based on plan type and billing period
          if (planType === 'pro') {
            finalPriceId = billingPeriod === 'year' ? priceIds.PRO_YEARLY : priceIds.PRO_MONTHLY;
          } else if (planType === 'business') {
            finalPriceId = billingPeriod === 'year' ? priceIds.BUSINESS_YEARLY : priceIds.BUSINESS_MONTHLY;
          } else {
            console.error(`Invalid plan type: ${planType}`);
            alert(`Invalid plan type: ${planType}`);
            setIsProcessingPayment(false);
            return;
          }
        }

        console.log(`Final price ID to use: ${finalPriceId}`);
        console.log(`All available price IDs:`, priceIds);

        // Track subscription attempt before redirecting
        trackSubscription('attempt', planType, billingPeriod);

        // Create session and redirect to checkout page
        await createAndRedirectToCheckout(
          finalPriceId,
          `${window.location.origin}/account-settings`,
          `${window.location.origin}/pricing`,
          planType
        );
      } catch (error) {
        console.error("Error during subscription:", error);

        // Call Stripe error handling function
        const canRetry = await handleStripeError(error);

        if (canRetry) {
          // Retry subscription after customer reset
          try {
            console.log("Retrying subscription after customer reset");

            // Similar code to main function, but simplified
            const origin = window.location.origin;

            // Select price ID based on plan type and billing period
            let finalPriceId;

            if (planType === 'transfer') {
              finalPriceId = priceIds.TRANSFER_ONETIME;
            } else if (planType === 'pro') {
              finalPriceId = billingPeriod === 'year' ? priceIds.PRO_YEARLY : priceIds.PRO_MONTHLY;
            } else if (planType === 'business') {
              finalPriceId = billingPeriod === 'year' ? priceIds.BUSINESS_YEARLY : priceIds.BUSINESS_MONTHLY;
            } else {
              throw new Error(`Invalid plan type: ${planType}`);
            }

            // Use helper function
            await createAndRedirectToCheckout(
              finalPriceId,
              `${origin}/account-settings?plan=${billingPeriod}`,
              `${origin}/pricing`,
              planType
            );

            return; // If successful, exit function before alert
          } catch (retryError) {
            console.error("Error during subscription retry:", retryError);
            // Continue with general error handling
          }
        }

        // Show error message to user
        alert(`Subscription error: ${error.message}`);
      } finally {
        setIsProcessingPayment(false);
      }
    },
    [priceIds, createAndRedirectToCheckout]
  );

  // Load settings on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load stripe products
        const products = await getProducts();
        setStripeProducts(products);

        // Load plans based on source setting
        if (useStripePlans) {
          const stripePlans = await getStripePlans(billingPeriod);
          if (stripePlans) {
            setPlans(stripePlans);
          } else {
            setPlans(getPricingPlans(billingPeriod));
          }
        } else {
          // Load local plans
          const updatedPlans = getPricingPlans(billingPeriod);
          setPlans(updatedPlans);
        }

        // Load active subscription
        if (auth.currentUser) {
          const subscription = await getSubscription();
          setActiveSubscription(subscription);
        }

        // Load developer mode setting
        try {
          const generalSettings = await getGeneralSettings();
          console.log('Developer mode setting:', generalSettings?.developerMode);
          setDeveloperMode(generalSettings?.developerMode || false);
        } catch (settingsError) {
          console.error('Error loading general settings:', settingsError);
          // Default to false if there's an error
          setDeveloperMode(false);
        }

      } catch (error) {
        console.error('Error loading pricing data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [billingPeriod, useStripePlans]);

  // Update pricing plans when billing period changes
  useEffect(() => {
    // Only apply this logic when using local plans
    if (useStripePlans) return;

    const localPlans = getPricingPlans(billingPeriod);

    // Add fixed price IDs based on billing period
    const updatedPlans = { ...localPlans };

    // Use fixed price IDs we defined
    if (billingPeriod === 'monthly') {
      if (updatedPlans.pro) updatedPlans.pro.stripePriceId = priceIds.PRO_MONTHLY;
      if (updatedPlans.business) updatedPlans.business.stripePriceId = priceIds.BUSINESS_MONTHLY;
      console.log('Using monthly price IDs:', {
        pro: priceIds.PRO_MONTHLY,
        business: priceIds.BUSINESS_MONTHLY
      });
    } else {
      if (updatedPlans.pro) updatedPlans.pro.stripePriceId = priceIds.PRO_YEARLY;
      if (updatedPlans.business) updatedPlans.business.stripePriceId = priceIds.BUSINESS_YEARLY;
      console.log('Using yearly price IDs:', {
        pro: priceIds.PRO_YEARLY,
        business: priceIds.BUSINESS_YEARLY
      });
    }

    // Set transfer price ID (one-time price)
    if (updatedPlans.transfer) {
      updatedPlans.transfer.stripePriceId = priceIds.TRANSFER_ONETIME;
      console.log('Using transfer one-time price ID:', priceIds.TRANSFER_ONETIME);
    }

    // If there are products from Stripe, set product IDs
    if (stripeProducts && stripeProducts.length > 0) {
      // Find products by IDs
      const proProduct = stripeProducts.find(product => product.id === STRIPE_PRODUCT_IDS.PRO);
      const businessProduct = stripeProducts.find(product => product.id === STRIPE_PRODUCT_IDS.BUSINESS);
      const transferProduct = stripeProducts.find(product => product.id === STRIPE_PRODUCT_IDS.TRANSFER);



      if (proProduct && updatedPlans.pro) {
        updatedPlans.pro.stripeProductId = proProduct.id;
        console.log('Set pro product ID:', proProduct.id);
      }

      if (businessProduct && updatedPlans.business) {
        updatedPlans.business.stripeProductId = businessProduct.id;
        console.log('Set business product ID:', businessProduct.id);
      }

      if (transferProduct && updatedPlans.transfer) {
        updatedPlans.transfer.stripeProductId = transferProduct.id;
        console.log('Set transfer product ID:', transferProduct.id);
      }
    } else {
      console.log('No products loaded from Stripe yet, using default IDs');
      // Set default values for product IDs
      if (updatedPlans.pro) updatedPlans.pro.stripeProductId = STRIPE_PRODUCT_IDS.PRO;
      if (updatedPlans.business) updatedPlans.business.stripeProductId = STRIPE_PRODUCT_IDS.BUSINESS;
      if (updatedPlans.transfer) updatedPlans.transfer.stripeProductId = STRIPE_PRODUCT_IDS.TRANSFER;
    }

    setPlans(updatedPlans);
  }, [billingPeriod, stripeProducts, priceIds, useStripePlans]);

  // Function to update price IDs
  const updatePriceId = useCallback((type, newPriceId, planType = 'pro') => {
    if (!isValidBillingPeriod(type)) {
      console.error('Invalid price type:', type);
      return;
    }

    // Check if plan type is valid
    if (!['pro', 'business', 'transfer'].includes(planType)) {
      console.error('Invalid plan type:', planType);
      return;
    }

    // Determine correct key for update
    let priceKey;

    if (planType === 'transfer') {
      priceKey = 'TRANSFER_ONETIME';
    } else if (planType === 'pro') {
      priceKey = type === 'monthly' ? 'PRO_MONTHLY' : 'PRO_YEARLY';
    } else { // business
      priceKey = type === 'monthly' ? 'BUSINESS_MONTHLY' : 'BUSINESS_YEARLY';
    }

    console.log(`Updating ${planType} ${type} price ID from ${priceIds[priceKey]} to ${newPriceId}`);

    // Update local state only
    setPriceIds(prev => ({
      ...prev,
      [priceKey]: newPriceId
    }));

    // Update plans if they match current type and period
    setPlans(prev => {
      const updatedPlans = { ...prev };
      // Update only if current billing period matches period being updated or if one-time product
      if (billingPeriod === type || planType === 'transfer') {
        updatedPlans[planType].stripePriceId = newPriceId;
      }
      return updatedPlans;
    });

    // Create a user-friendly message showing what was updated
    let planDisplayName;
    switch (planType) {
      case 'pro': planDisplayName = 'Pro'; break;
      case 'business': planDisplayName = 'Business'; break;
      case 'transfer': planDisplayName = 'Transfer'; break;
      default: planDisplayName = planType;
    }

    let billingTypeMsg;
    if (planType === 'transfer') {
      billingTypeMsg = 'one-time';
    } else {
      billingTypeMsg = type === 'monthly' ? 'Monthly' : 'Yearly';
    }

    alert(`${billingTypeMsg} price ID for ${planDisplayName} updated to: ${newPriceId}`);
  }, [billingPeriod]);

  // Helper functions for price handling

  // Create price info object
  const createPriceInfo = (price, product) => {
    const priceInfo = {
      id: price.id,
      productId: product.id,
      productName: product.name || 'No Name',
      active: price.active,
      amount: price.unit_amount / 100,
      currency: price.currency,
      isConfigured: false
    };

    // Identify predefined prices in system
    if (price.id === priceIds.PRO_MONTHLY) {
      priceInfo.isConfigured = true;
      priceInfo.configuredFor = 'Pro Monthly';
    } else if (price.id === priceIds.PRO_YEARLY) {
      priceInfo.isConfigured = true;
      priceInfo.configuredFor = 'Pro Yearly';
    } else if (price.id === priceIds.BUSINESS_MONTHLY) {
      priceInfo.isConfigured = true;
      priceInfo.configuredFor = 'Business Monthly';
    } else if (price.id === priceIds.BUSINESS_YEARLY) {
      priceInfo.isConfigured = true;
      priceInfo.configuredFor = 'Business Yearly';
    } else if (price.id === priceIds.TRANSFER_ONETIME) {
      priceInfo.isConfigured = true;
      priceInfo.configuredFor = 'Transfer One-time';
    }

    // Determine price type
    if (price.recurring) {
      priceInfo.interval = price.recurring.interval;
    } else {
      priceInfo.interval = 'one-time';
    }

    return priceInfo;
  };

  // Categorize prices by category
  const categorizePrices = (products) => {
    const monthlyPrices = [];
    const yearlyPrices = [];
    const otherPrices = [];

    products.forEach(product => {
      if (product.prices && product.prices.length > 0) {
        product.prices.forEach(price => {
          const priceInfo = createPriceInfo(price, product);

          // Categorize by price type
          if (price.recurring) {
            if (price.recurring.interval === 'month') {
              monthlyPrices.push(priceInfo);
            } else if (price.recurring.interval === 'year') {
              yearlyPrices.push(priceInfo);
            } else {
              otherPrices.push(priceInfo);
            }
          } else {
            otherPrices.push(priceInfo);
          }
        });
      }
    });

    return { monthlyPrices, yearlyPrices, otherPrices };
  };

  // Create detailed text for price list
  const createPriceListText = (prices, title) => {
    let text = `=== ${title} ===\n`;

    if (prices.length === 0) {
      text += 'No prices in this category\n\n';
      return text;
    }

    prices.forEach(price => {
      text += `• ${price.isConfigured ? '✓ ' : ''}ID: ${price.id}\n`;
      text += `  Product: ${price.productName} (${price.productId})\n`;
      text += `  Price: ${price.amount} ${price.currency}\n`;
      text += `  Status: ${price.active ? 'Active' : 'Inactive'}\n`;
      if (price.isConfigured) {
        text += `  Configured for: ${price.configuredFor}\n`;
      }
      if (price.interval !== 'month' && price.interval !== 'year') {
        text += `  Type: ${price.interval}\n`;
      }
      text += '\n';
    });

    return text;
  };

  // Create price ID summary
  const createPriceIdSummary = (priceIds) => {
    let summary = '=== Configured Price IDs in System ===\n';

    summary += `• Pro Monthly: ${priceIds.PRO_MONTHLY}\n`;
    summary += `• Pro Yearly: ${priceIds.PRO_YEARLY}\n`;
    summary += `• Business Monthly : ${priceIds.BUSINESS_MONTHLY}\n`;
    summary += `• Business Yearly : ${priceIds.BUSINESS_YEARLY}\n`;
    summary += `• Transfer One-time: ${priceIds.TRANSFER_ONETIME}\n\n`;
    return summary;
  };

  // Check for missing price IDs
  const checkMissingPriceIds = (definedPriceIds, allPriceIds) => {
    const missingPriceIds = definedPriceIds.filter(id => !allPriceIds.includes(id));
    return missingPriceIds;
  };

  // Function to check all prices
  const checkAllPrices = useCallback(async () => {
    console.log('Checking all prices...');
    setLoading(true);

    try {
      const allProducts = await getAllPrices();
      console.log('All products with prices:', allProducts);

      // Categorize prices
      const { monthlyPrices, yearlyPrices, otherPrices } = categorizePrices(allProducts);

      // Create detailed text for each category
      const monthlyPricesText = createPriceListText(monthlyPrices, 'Monthly Prices');
      const yearlyPricesText = createPriceListText(yearlyPrices, 'Yearly Prices');
      const otherPricesText = createPriceListText(otherPrices, 'Other Prices');

      // Create price ID summary
      const configSummary = createPriceIdSummary(priceIds);

      // Show full summary
      const fullReport = configSummary + monthlyPricesText + yearlyPricesText + otherPricesText;

      console.log('Price report:', fullReport);

      // Show summary in window
      alert(fullReport);

      // Check configured price IDs
      const definedPriceIds = [
        priceIds.PRO_MONTHLY,
        priceIds.PRO_YEARLY,
        priceIds.BUSINESS_MONTHLY,
        priceIds.BUSINESS_YEARLY,
        priceIds.TRANSFER_ONETIME
      ];

      // Create list of all price IDs
      const allPriceIds = [...monthlyPrices, ...yearlyPrices, ...otherPrices].map(p => p.id);

      // Check for missing price IDs
      const missingPriceIds = checkMissingPriceIds(definedPriceIds, allPriceIds);

      if (missingPriceIds.length > 0) {
        const missingMessage = `Note: The following IDs are defined in the system but not in Stripe:\n${missingPriceIds.join('\n')}`;
        console.warn(missingMessage);
        alert(missingMessage);
      }
    } catch (error) {
      console.error('Error checking prices:', error);
      alert(`Error checking prices: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [priceIds]);

  return {
    billingPeriod,
    toggleBillingPeriod,
    plans,
    loading,
    isProcessingPayment,
    handleSubscribe,
    activeSubscription,
    checkAllPrices,
    updatePriceId,
    developerMode,
    useStripePlans,
    togglePlanSource
  };
};

export default usePricingLogic; 